/**
 * Escritas da página "Quem me deve" pelos repositórios do app: valor a receber (criar, editar,
 * excluir), recebimento (com excedente), baixa, cronograma/data combinada e renomear a pessoa.
 */
import {
  createPersonalLoan, deletePersonalLoan, deletePersonalLoanEvent, getPersonalLoanEvents, recordPersonalLoanReceipt, recordPersonalLoanWriteOff,
  renamePerson, reschedulePersonalLoan, updatePersonalLoan,
} from '@/db/repositories/personalLoanRepository';
import type { LoansEditor } from '../viewer/lib/editing';

/** ids dos eventos do valor a receber (o repositório devolve o valor, não o evento criado — o Desfazer precisa do id) */
async function eventIds(loanId: string): Promise<Set<string>> {
  return new Set((await getPersonalLoanEvents(loanId)).map(e => e.id));
}

export function createLoansEditor(after: () => Promise<void>): LoansEditor {
  return {
    async create(input) {
      const l = await createPersonalLoan({
        personId: input.personId, personName: input.personName, accountId: input.isExisting ? null : input.accountId,
        amount: input.amount, date: input.date,
        agreedDate: input.expect === 'date' ? input.agreedDate : null,
        schedule: input.expect === 'schedule' ? input.schedule : null,
        notes: input.notes, isExisting: input.isExisting,
      });
      await after();
      return l.id;
    },
    async update(id, input) {
      await updatePersonalLoan({ loanId: id, amount: input.amount, date: input.date, accountId: input.accountId, notes: input.notes });
      await after();
    },
    async remove(id) { await deletePersonalLoan(id); await after(); },
    async receipt(input) {
      const was = await eventIds(input.loanId);
      await recordPersonalLoanReceipt({
        loanId: input.loanId, date: input.date, amount: input.amount, accountId: input.isExistingReceipt ? null : input.accountId,
        isExistingReceipt: input.isExistingReceipt, excessCategoryId: input.excessCategoryId, excessSubcategoryId: input.excessSubcategoryId, notes: input.notes,
      });
      const id = [...await eventIds(input.loanId)].find(x => !was.has(x)) ?? null;
      await after();
      return id;
    },
    async writeOff(input) {
      const was = await eventIds(input.loanId);
      await recordPersonalLoanWriteOff({ loanId: input.loanId, date: input.date, amount: input.amount, categoryId: input.categoryId, subcategoryId: input.subcategoryId, notes: input.notes });
      const id = [...await eventIds(input.loanId)].find(x => !was.has(x)) ?? null;
      await after();
      return id;
    },
    async reschedule(input) {
      // "quando der" = lista vazia; data única = só agreedDate (schedule null); cronograma = as linhas.
      await reschedulePersonalLoan({
        loanId: input.loanId,
        agreedDate: input.expect === 'date' ? input.agreedDate : null,
        schedule: input.expect === 'schedule' ? input.schedule : input.expect === 'when' ? [] : null,
      });
      await after();
    },
    async removeEvent(id) { await deletePersonalLoanEvent(id); await after(); },
    async renamePerson(personId, name) { await renamePerson(personId, name); await after(); },
  };
}
